import * as clusterApi from '@/api/cluster';
import * as jobApi from '@/api/job';
import RemoteClusterDialog from '@/components/remoteClusterDialog';
import { BottomTabsKey, useTabKeyStore } from '@/utils/bottomTabsKeyStore';
import useFileStore, { PaneType } from '@/utils/fileStore';
import { useJobStore } from '@/utils/jobService';
import { useSplitterStore } from '@/utils/splitterStore';
import { useRequest } from 'ahooks';
import { Alert, App, Button, Form, FormProps, Input, InputNumber, Modal, Select, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';
import pathbe from 'path-browserify-esm';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const parseWalltimeToMinutes = (walltime?: string): number => {
  if (!walltime) return 1440; // default value (24 hours in minutes)
  const parts = walltime.split(':');
  if (parts.length !== 3) return 1440;
  const hours = parseInt(parts[0], 10);
  const minutes = parseInt(parts[1], 10);
  const seconds = parseInt(parts[2], 10);
  return hours * 60 + minutes + seconds / 60;
};

const JobSubmissionDialog = ({ visible, onClose }: {
  visible: boolean
  onSubmit?: (path: string, jobName: string) => void
  onClose: () => void
}) => {
  const { message } = App.useApp();
  const machineMapRef = useRef<{ [partition: string]: string[] }>();
  // Cluster selection. `clusters` is empty when multi-cluster support is off, which is the
  // signal to hide the selector entirely rather than show a single-option dropdown.
  const [clusters, setClusters] = useState<clusterApi.ClusterView[]>([]);
  const [clusterId, setClusterId] = useState<string>('');
  const [remoteUser, setRemoteUser] = useState<string | undefined>(undefined);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [partitionOptions, setPartitionOptions] = useState<{ label: string, value: string }[]>([]);
  const [nodesOptions, setNodesOptions] = useState<{ label: string, value: string }[]>([]);
  const [form] = Form.useForm<{
    cpuNum: number,
    gpuNum: number,
    memory: number,
    jobName: string,
    partition?: string,
    node?: string,
    maxTime?: number
  }>();
  const { t } = useTranslation('common');

  const { setVerticalSizes } = useSplitterStore();
  const setBottomTabsActiveKey = useTabKeyStore(state => state.setBottomTabsActiveKey);
  const setOutputTabsActiveKey = useTabKeyStore(state => state.setOutputTabsActiveKey);

  const fetchClusters = async () => {
    const res = await clusterApi.getClusters();
    // An absent or failed response must not be read as "no clusters": that would hide the
    // selector on a deployment that has remote clusters configured. Fall back to keeping
    // whatever was there and let the submit path report the real error.
    if (!res?.ok) return;
    const list = res.data || [];
    setClusters(list);
    if (list.length > 0) {
      setClusterId(prev => {
        if (prev && list.some(c => c.id === prev)) return prev;
        const def = list.find(c => c.isDefault) || list[0];
        return def.id;
      });
    }
  };

  const selectedCluster = clusters.find(c => c.id === clusterId);
  // A remote cluster the user has not connected yet cannot accept a submission. Say so and
  // offer the wizard, rather than letting the submit fail with a transport error.
  const needsConnect = !!selectedCluster?.remote && !selectedCluster?.myAccessOk;
  const accounts = (selectedCluster?.accounts || []).filter(a => a.state === 'verified');
  // Shown only when there is a real choice. The backend now always reports the local
  // cluster, including when its table has not been migrated, so an empty list no longer
  // means "single cluster" and `clusters.length > 0` is the wrong condition. A deployment
  // with only the local cluster would otherwise gain a one-option dropdown.
  const showClusterPicker = clusters.some(c => c.remote) || clusters.length > 1;

  const fetchMachineInfo = async (cluster?: string) => {
    const res = await jobApi.getMachineList(cluster);
    if (res?.ok) {
      const data = res?.data || [];
      machineMapRef.current = res.data;
      const PartitionOptions = Object.keys(data).map((partition) => ({
        label: partition,
        value: partition
      }))
      PartitionOptions.unshift({ label: t('job_submission.default'), value: 'default' })
      // A remote cluster reports its own default partition; honour it instead of forcing
      // 'default', which the remote scheduler has never heard of.
      form.setFieldValue('partition', selectedCluster?.defaultPartition || 'default')
      setPartitionOptions(PartitionOptions)
      machineMapRef.current = data
    } else {
      // message.error(res?.message || 'Failed to get machine info');
    }
  }

  const updateNodeOptions = (partition: string) => {
    if (!machineMapRef.current) {
      return;
    }

    if (partition === 'default') {
      setNodesOptions([]);
      form.setFieldsValue({ 'node': 'default' });
      return;
    }

    const nodes = machineMapRef.current[partition].map((node) => ({
      label: node,
      value: node
    }));
    nodes.unshift({ label: t('job_submission.default'), value: 'default' });
    console.log("nodes", nodes)
    setNodesOptions(nodes);
    form.setFieldsValue({ 'node': 'default' });
  }
  const activePanes = useFileStore(state => state.activePanes);

  const selectedFilePath = activePanes[0]?.path;

  useEffect(() => {
    if (visible) {
      fetchClusters();
      fetchMachineInfo(clusterId || undefined);
      form.setFieldValue('jobName', selectedFilePath?.split('/').pop())
    } else {
      form.resetFields();
      setNodesOptions([]);
    }
    // clusterId is intentionally not a dependency: changing cluster already refetches in
    // the selector's onChange, and adding it here would refetch on the initial set too.
  }, [selectedFilePath, visible]);


  const handleClose = () => {
    form.resetFields();
    setNodesOptions([]);
    onClose();
  }

  const updateFolder = useFileStore(state => state.updateFolder);

  const submitJob = useJobStore(state => state.submitJob);
  const { run: submitJobRequest, loading } = useRequest(submitJob, {
    manual: true,
    onSuccess: (isSuccess, params) => {
      if (isSuccess) {
        handleClose()
        if (isSuccess) {
          message.success(t('job_submission.submit_success'))
          setVerticalSizes(["80%", "20%"])
          setBottomTabsActiveKey(BottomTabsKey.OUTPUT)
          if (params[0]?.comment) {
            setOutputTabsActiveKey(params[0]?.comment)
          }
        }
      } else {
        message.error(t('job_submission.submit_failed'))
      }
    },
    onError: () => {
      message.error(t('job_submission.submit_failed'))
    }
  })

  const onFileSave = useFileStore(state => state.onFileSave);

  const handleSave = async () => {
    const pane = activePanes[0];
    if (!pane?.key) return false;
    const success = await onFileSave(pane.key);

    return success;
  }

  const handleOk = async () => {
    if (!selectedFilePath) {
      message.error(t('job_submission.no_file'));
      return;
    }
    if (activePanes[0].type === PaneType.TEMP_FILE) {
      message.info(t('job_submission.save_first'));
      return;
    }

    if (activePanes[0].modified) {
      const isSuccess = await handleSave();
      if (!isSuccess) {
        message.error(t('job_submission.save_failed'));
        return;
      }
    }

    const { cpuNum, gpuNum, jobName, memory, partition, node, maxTime } = form.getFieldsValue();
    const user = localStorage.getItem('userName');
    const tag = user + "_" + dayjs().unix().toString();
    const param = {
      cluster: clusterId || undefined,
      remote_user: remoteUser,
      path: selectedFilePath,
      cpu_num: cpuNum ?? 1,
      gpu_num: gpuNum ?? 0,
      memory: memory ?? 1024,
      jobName,
      partition: partition === 'default' ? undefined : partition,
      node: node === 'default' ? undefined : node,
      comment: tag,
      max_time: maxTime
    }
    updateFolder(pathbe.dirname(selectedFilePath))
    await submitJobRequest(param)
  };

  const { run: queryUserResource, data: userResource, loading: userResourceLoading } = useRequest(jobApi.queryUserResource, {
    onSuccess: (res) => {
      if (res?.ok) {
        console.log("User resource data:", userResource);
      }
    }
  })

  const onFormChange: FormProps['onFieldsChange'] = (changedFields) => {
    if (changedFields[0].name[0] === 'partition') {
      updateNodeOptions(changedFields[0].value)
      const { node } = form.getFieldsValue();
      queryUserResource({ partition: changedFields[0].value, node, cluster: clusterId })
      console.log("changedFields:", changedFields)
      if (changedFields[0].value === "gpu") {
        form.setFieldValue('gpuNum', 1)
      } else {
        form.setFieldValue('gpuNum', 0)
      }
    } else if (changedFields[0].name[0] === 'node') {
      const { partition } = form.getFieldsValue();
      queryUserResource({ partition, node: changedFields[0].value, cluster: clusterId })
    }
  }

  return (
    <Modal
      title={t('job_submission.title')}
      open={visible}
      onOk={handleOk}
      okButtonProps={{ loading: loading || userResourceLoading, disabled: needsConnect }}
      onCancel={handleClose}
    >
      <Form layout="vertical" form={form} initialValues={{ cpuNum: 1, gpuNum: 0, jobName: "", partition: 'default' }} onFieldsChange={onFormChange}>
        {showClusterPicker && (
          <Form.Item label={t('job_submission.cluster')}>
            <Select
              value={clusterId || undefined}
              placeholder={t('job_submission.cluster_placeholder')}
              onChange={(value) => {
                setClusterId(value);
                setRemoteUser(undefined);
                const next = clusters.find(c => c.id === value);
                // Partition and node lists are cluster-specific; refetch rather than leave
                // the previous cluster's options selectable.
                setPartitionOptions([]);
                setNodesOptions([]);
                machineMapRef.current = undefined;
                fetchMachineInfo(value);
                if (next?.defaultPartition) form.setFieldValue('partition', next.defaultPartition);
              }}
              options={clusters.map(c => ({
                label: c.hostReachable === false
                  ? `${c.displayName} (${t('job_submission.cluster_unreachable')})`
                  : c.displayName,
                value: c.id,
                disabled: c.hostReachable === false
              }))}
            />
          </Form.Item>
        )}
        {selectedCluster?.remote && accounts.length > 1 && (
          <Form.Item
            label={t('job_submission.remote_account')}
            tooltip={t('job_submission.remote_account_hint')}
          >
            <Select
              value={remoteUser ?? (accounts.find(a => a.isDefault) || accounts[0])?.remoteUser}
              onChange={setRemoteUser}
              options={accounts.map(a => ({ label: a.remoteUser, value: a.remoteUser }))}
            />
          </Form.Item>
        )}
        {needsConnect && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('job_submission.not_connected_title')}
            description={selectedCluster?.myAccessMessage || t('job_submission.not_connected_body')}
            action={<Button size="small" type="primary" onClick={() => setWizardOpen(true)}>{t('job_submission.connect_now')}</Button>}
          />
        )}
        <Form.Item label={t('job_submission.current_file')}>
          <Tooltip title={selectedFilePath} placement="topLeft">
            <Tag style={{
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'inline-block'
            }}>
              {selectedFilePath}
            </Tag>
          </Tooltip>
        </Form.Item>
        <Form.Item label={t('job_submission.job_name')} name={'jobName'}>
          <Input />
        </Form.Item>
        <Form.Item label={t('job_submission.partition')} name={'partition'}>
          <Select placeholder={t('job_submission.partition_placeholder')} options={partitionOptions}>
          </Select>
        </Form.Item>
        {form.getFieldValue('partition') !== "default" ? <>
          <Form.Item label={t('job_submission.max_time')} name={'maxTime'} dependencies={['partition']}>
            <InputNumber disabled={form.getFieldValue('partition') === "default"} min={1} max={userResource?.data?.max_walltime && userResource?.data?.max_walltime !== "unlimited" ? parseWalltimeToMinutes(userResource?.data?.max_walltime) : Number.MAX_SAFE_INTEGER} />
          </Form.Item>
          <Form.Item label={t('job_submission.cpu_count')} name={'cpuNum'} dependencies={['partition']}>
            <InputNumber disabled={form.getFieldValue('partition') === "default"} min={1} max={userResource?.data?.max_cpus || 1} />
          </Form.Item>
          <Form.Item label={t('job_submission.gpu_count')} name={'gpuNum'} dependencies={['partition']}>
            <InputNumber disabled={form.getFieldValue('partition') === "default"} min={0} max={userResource?.data?.max_cpus || 0} />
          </Form.Item>
          <Form.Item label={t('job_submission.memory')} name={'memory'} dependencies={['partition']}>
            <InputNumber disabled={form.getFieldValue('partition') === "default"} min={0} />
          </Form.Item>
        </> : <></>}
        <Form.Item label={t('job_submission.node')} name={'node'} dependencies={['partition']} >
          <Select placeholder={t('job_submission.node_placeholder')} disabled={nodesOptions.length === 0} options={nodesOptions}>
          </Select>
        </Form.Item>
      </Form>
      <RemoteClusterDialog
        open={wizardOpen}
        clusterId={clusterId}
        clusters={clusters}
        onClose={() => setWizardOpen(false)}
        onConnected={() => {
          setWizardOpen(false);
          // Re-read the directory: the cluster the user just connected becomes selectable,
          // and its cached partition snapshot arrives with it.
          fetchClusters();
          fetchMachineInfo(clusterId || undefined);
        }}
      />
    </Modal>
  );
};

export default JobSubmissionDialog;